import { Alert, Box, Stack, Typography } from "@mui/material";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import {
  SSO_PLATFORM_LABELS,
  SSO_PROTOCOL_LABELS,
  SsoPlatform,
  SsoSetup,
} from "@/services/features/SsoProviderService";
import { formatInteger } from "@/utils/number";

const PLATFORMS: SsoPlatform[] = ["WEB", "IOS", "ANDROID"];

function SetupUriList({ entries }: { entries: { label: string; uri: string }[] }) {
  return (
    <Stack component="dl" spacing={1.5} sx={{ m: 0 }}>
      {entries.map(({ label, uri }) => (
        <Box component="div" key={label}>
          <Typography component="dt" variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
          <Box component="dd" sx={{ m: 0 }}>
            <Box
              component="pre"
              dir="ltr"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "action.hover",
                fontFamily: "monospace",
                fontSize: "0.8125rem",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                userSelect: "text",
              }}
            >
              {uri}
            </Box>
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

export default function SsoSetupDetails({ setup }: { setup: SsoSetup }) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const numberLocale = new Intl.Locale(i18n.resolvedLanguage || i18n.language || "en", {
    numberingSystem: "latn",
  });
  const configured = PLATFORMS.filter(
    (platform) => (setup.redirectUris[platform] ?? []).length > 0,
  );
  const saml = setup.samlEntityId && setup.samlAssertionConsumerUrl;
  return (
    <Stack
      spacing={3}
      sx={{
        "& code": {
          fontFamily: "monospace",
          fontSize: "0.9em",
          fontWeight: 500,
          bgcolor: (theme) => theme.palette.grey[theme.palette.mode === "dark" ? 800 : 100],
          color: (theme) =>
            theme.palette.mode === "dark" ? theme.palette.error.light : theme.palette.error.dark,
          borderRadius: 1,
          px: 0.5,
          py: 0.125,
          whiteSpace: "nowrap",
        },
      }}
    >
      <Typography variant="body2">
        Ask the customer&apos;s identity team whether they will register Crave as an OpenID Connect
        or a SAML 2.0 application, and for an existing Crave user&apos;s email for testing. Name the
        provider after the customer&apos;s organisation, since employees see it as &ldquo;Continue
        with &hellip;&rdquo; on the login page.
      </Typography>
      <Stack component="section" aria-labelledby={`${id}-oidc`} spacing={2}>
        <Typography
          id={`${id}-oidc`}
          component="h3"
          variant="h6"
          sx={{ fontSize: "1.25rem", fontWeight: 700 }}
        >
          {SSO_PROTOCOL_LABELS.OIDC}
        </Typography>
        <Typography variant="body2">
          Ask for their issuer URL, client ID, client secret and token endpoint authentication
          method, and arrange secure delivery of the secret. Register every callback below for the
          platforms the client serves, exactly as shown. A client with its own redirect URI override
          uses that instead.
        </Typography>
        {configured.length > 0 ? (
          <Stack spacing={2.5}>
            {configured.map((platform) => (
              <Stack
                component="section"
                aria-labelledby={`${id}-${platform}`}
                key={platform}
                spacing={1.5}
              >
                <Typography
                  id={`${id}-${platform}`}
                  component="h4"
                  variant="subtitle1"
                  sx={{ fontWeight: 700 }}
                >
                  {platform === "WEB"
                    ? t("management.sso.setup.platforms.web")
                    : SSO_PLATFORM_LABELS[platform]}
                </Typography>
                <SetupUriList
                  entries={(setup.redirectUris[platform] ?? []).map((uri, index) => ({
                    label: t("management.sso.setup.redirectUri", {
                      index: formatInteger(index + 1, numberLocale),
                    }),
                    uri,
                  }))}
                />
              </Stack>
            ))}
          </Stack>
        ) : (
          <Alert severity="warning">
            No SSO callback URLs are defined for this API environment. Crave engineering must add
            them in the API before a provider can be enabled.
          </Alert>
        )}
        <Typography variant="body2">
          Register a confidential OpenID Connect application using the authorization code grant and
          S256 PKCE. Allow the scopes openid, email, and profile, and sign ID tokens using RS256.
          The ID token must include <code>sub</code>, <code>iss</code>, <code>aud</code>,{" "}
          <code>iat</code>, <code>exp</code>, <code>nonce</code>, <code>email</code>, and boolean{" "}
          <code>email_verified: true</code>. The verified email must match an existing user in this
          customer. Claims supplied only by UserInfo are not supported.
        </Typography>
      </Stack>
      <Stack component="section" aria-labelledby={`${id}-saml`} spacing={2}>
        <Typography
          id={`${id}-saml`}
          component="h3"
          variant="h6"
          sx={{ fontSize: "1.25rem", fontWeight: 700 }}
        >
          {SSO_PROTOCOL_LABELS.SAML}
        </Typography>
        {saml ? (
          <>
            <Typography variant="body2">
              Give them the values below (in Microsoft Entra: Identifier, Reply URL). Leave the
              sign-on URL, relay state and logout URL blank. Ask for their federation metadata URL
              and their identity provider&apos;s entity ID in return.
            </Typography>
            <SetupUriList
              entries={[
                { label: t("management.sso.setup.saml.entityId"), uri: setup.samlEntityId ?? "" },
                {
                  label: t("management.sso.setup.saml.replyUrl"),
                  uri: setup.samlAssertionConsumerUrl ?? "",
                },
                ...(setup.samlMetadataUrl
                  ? [{ label: t("management.sso.setup.saml.metadata"), uri: setup.samlMetadataUrl }]
                  : []),
              ]}
            />
            <Typography variant="body2">
              The assertion must be signed with SHA-256 and not encrypted, and must carry the
              user&apos;s email in the <code>emailaddress</code> claim (Entra&apos;s default{" "}
              <code>user.mail</code> mapping). The email must match an existing user in this
              customer, and users or groups must be assigned to the application.
            </Typography>
          </>
        ) : (
          <Alert severity="info">
            This API environment does not publish SAML service provider values yet.
          </Alert>
        )}
      </Stack>
      <Typography variant="body2">
        Compatibility depends on the provider&apos;s configuration. OAuth-only connections are not
        supported. Existing passwords, user roles, and store permissions remain in place.
      </Typography>
    </Stack>
  );
}
